import * as Sentry from '@sentry/nextjs';
import NewsCTA from 'components/buttons/newscta';
import Layout, { DATA_TYPES } from 'components/layout';
import PortableText from 'components/portableText';
import ShareBlock from 'components/Sharing';
import { differenceInHours } from 'date-fns';
import { getAllPostPathComponents, getPost } from 'lib/api/posts';
import ErrorPage from 'next/error';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { toPlainText } from 'utilities/blockText';
import { BlogPostDetailType } from 'utilities/propTypes';

const REVALIDATE_TIME_MINUTES = 5;
const REVALIDATE_TIME_SECONDS = REVALIDATE_TIME_MINUTES * 60;
const NUM_BLOGS_TO_RENDER = 3;

export const getStaticProps = Sentry.wrapGetStaticPropsWithSentry(
  async ({ params, preview = false }) => {
    const post = await getPost({
      year: params.year,
      month: params.month,
      day: params.day,
      slug: params.slug,
      preview,
    });
    if (!post) {
      return {
        notFound: true,
      };
    } else {
      return {
        props: {
          preview,
          post: post,
        },
        revalidate: REVALIDATE_TIME_SECONDS,
      };
    }
  }
);

export const getStaticPaths = async () => {
  const paths = await getAllPostPathComponents();
  return {
    paths: paths.slice(0, NUM_BLOGS_TO_RENDER),
    fallback: 'blocking',
  };
};

const UPDATED_MIN_TIME_HOURS = 24; // Give a little buffer in case we make small edits after publishing.
const BlockText = ({ blocks }) => <PortableText blocks={blocks} />;
const DATE_OPTIONS = {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
};

const BlogPostDetail = ({ post }) => {
  const router = useRouter();
  if (!router.isFallback && !post?.slug) {
    return <ErrorPage statusCode={404} />;
  }

  if (router.isFallback) {
    return <div>Loading...</div>;
  }

  const publishedAtDate = new Date(post.publishedAt); // 2022-03-02T13:07:09.844-05:00
  const lastUpdatedDate = new Date(post.lastUpdated); // 2022-03-18T19:48:38.463Z
  const wasUpdated =
    differenceInHours(lastUpdatedDate, publishedAtDate) >
    UPDATED_MIN_TIME_HOURS;

  const createdDateFormatted = publishedAtDate.toLocaleDateString(
    'en-us',
    DATE_OPTIONS
  );
  const lastUpdatedFormatted = lastUpdatedDate.toLocaleDateString(
    'en-us',
    DATE_OPTIONS
  );

  const descriptionPlainText = toPlainText(post.description);

  return (
    <Layout
      title={post.title}
      dataType={DATA_TYPES.blog}
      description={descriptionPlainText}
      keywords={post.tags}
      image={post.mainImage.asset.url}
      imageAltText={post.mainImage.alt}
      publishedOn={post.publishedAt}
      updatedOn={wasUpdated ? post.lastUpdated : null}
      canonicalPath={`/news/${router.query.year}/${router.query.month}/${router.query.day}/${router.query.slug}`}
    >
      <div className="mx-auto max-w-prose">
        <span className="text-xs tracking-wide uppercase">
          Published {createdDateFormatted}
        </span>
        {wasUpdated ? (
          <span className="text-xs tracking-wide uppercase">
            &nbsp;/ Updated {lastUpdatedFormatted}
          </span>
        ) : null}
        <h1 className="my-2">{post.title}</h1>
        <div className="mb-2">
          <BlockText blocks={post.description} />
        </div>
        {post.authors && (
          <p className="mb-2">
            <em>by</em> {post.authors.map((a) => a.name).join(', ')}
          </p>
        )}
        <ShareBlock pathname={router.asPath} text={post.title} />
        <div className="my-5 prose dark:prose-invert">
          <BlockText blocks={post.body} />
        </div>
      </div>
      <NewsCTA />
    </Layout>
  );
};

export default BlogPostDetail;

BlockText.propTypes = {
  blocks: PropTypes.arrayOf(PropTypes.shape({})),
};

BlogPostDetail.propTypes = BlogPostDetailType;
